// FOR THINGS USED IN BOTH BUILDER AND REPLY GRIDS

import cloneDeep from 'lodash/cloneDeep';
import { hasValueOrZero, toNumberOrNull } from '../../js/filters-helpers';
import { vendorColsAll } from './quote-sheet-vendor-cols';



// CLASSES
class QuoteSheet {
    constructor({  // make sure there are defaults for everything... we'll instantiate empty one before we get real one from API
        id = '',
        title = '',
        instructions = '',
        currency = 'USD',
        currency_name = '',  // optional, will use this if passed in, otherwise we set it based on currency and currency_choices
        currency_choices,  // READ ONLY - used to populate select box in QuoteSheet builder, and to get currency_name if not passed directly
        quoted_currency_options_codes,  // Array of currency CODES for allowed currency on quote replies - added May 2023 for multi-currency support - should at least include the 'currency' code
        has_multiple_quoted_currency_options,  // should be boolean of whether we allow supplier selected currency 
        enable_price = true,
        enable_subtotal = false,  // line item total (quantity * price or just price if no quantity)
        enable_quoted_price = false,
        enable_comment = true,
        enable_moq = false,
        enable_leadtime = false,
        enable_deliverydate = false,
        enable_country_of_origin = false,  // added backend field for this - Oct 2024
        enable_shipterms = false,
        enable_payterms = false,
        enable_nobid = true,
        shipterms_choices = [],
        payterms_choices = [],
        country_of_origin_choices = [],  // added backend field for this - Oct 2024 - will be {code, name} objects
        show_name = true,  // added backend field for this - Aug 9 2022
        show_part_number = true,
        show_description = true,
        show_quantity = true,
        show_uom = true,
        show_timeframe = true,
        show_need_by_date = false,  
        // TODO - make sure show_current_price and show_target_price are not on for external usage
        show_current_price = false, // false as default, simply because these were hidden until Sept 2025, and so we don't show if not explicitly specified (because this is also used for external views)
        show_target_price = false,  // false as default, simply because these were hidden until Sept 2025, and so we don't show if not explicitly specified (because this is also used for external views)
        is_template,  // leave it as undefined if not set
        template_slug = '',
        //has_submitted_replies,  // add this if/when you have a use for it in the class instance
    }) {
        console.log('From the top of QuoteSheet constructor, enable_deliverydate is:');
        console.log(enable_deliverydate.toString());
        this.id = id;
        this.title = title;
        this.instructions = instructions;
        this.currency = currency;
        this.currency_choices = currency_choices || [{}];  
        if (currency_name) {
            this.currency_name = currency_name;
        } else {  // if we didn't explicitly set currency_name...
            this.currency_name = (!!currency && !!currency_choices && Array.isArray(currency_choices)) ? currency_choices.find(curObj => curObj.value === currency)['text'] : '';
            // if we have currency AND currency_choices array, find the currency object {value, text} and return the text, set as this.currency_name
            // default to empty string if we don't have enough info
        }
        this.quoted_currency_options_codes = quoted_currency_options_codes && Array.isArray(quoted_currency_options_codes) ? [...quoted_currency_options_codes] : [this.currency];  // should already include this.currency anyways, from backend
        this.has_multiple_quoted_currency_options = has_multiple_quoted_currency_options ?? false;
        this.is_template = is_template;
        this.template_slug = template_slug;
        // this.has_submitted_replies = has_submitted_replies;  // add if/when you have a use for it
        // DEAL WITH COUNTRY OF ORIGIN CHOICES -- we reference these in various consumers of this class... so we need to make sure they're in the right format
        const countryOfOriginChoices = country_of_origin_choices && Array.isArray(country_of_origin_choices) ? [...country_of_origin_choices] : [];
        this.country_of_origin_choices = country_of_origin_choices;  // added backend field for this - Oct 2024 - will be {code, name} objects
        this.countryCodes = countryOfOriginChoices.map(c => c.code);  // just the codes
        this.countryNames = countryOfOriginChoices.map(c => c.name);  // just the names... useful for filtering in the agRichSelectCellEditor
        this.countryNamesByCode = Object.fromEntries(countryOfOriginChoices.map(c => [c.code, c.name]));
        this.countryCodesByName = Object.fromEntries(countryOfOriginChoices.map(c => [c.name, c.code]));
        // GROUP THINGS TO MAKE MANAGING METADATA EASIER IN VUE
        // these are OK as booleans, no need to map to 'on'/'off' since they're not in grid cells (we removed on/off anyways)
        this.vendorCols = {
            enable_price: enable_price,
            enable_subtotal: enable_subtotal,
            enable_quoted_price: enable_quoted_price,
            enable_quoted_subtotal: enable_subtotal,  // TODO - enable when ready - enable_subtotal,  // just match the normal price subtotal setting 
            enable_comment: enable_comment,
            enable_moq: enable_moq,
            enable_leadtime: enable_leadtime,
            enable_deliverydate: enable_deliverydate,
            enable_country_of_origin: enable_country_of_origin,  // added backend field for this - Oct 2024
            enable_shipterms: enable_shipterms,
            enable_payterms: enable_payterms,
            enable_nobid: enable_nobid,
            shipterms_choices: shipterms_choices,
            payterms_choices: payterms_choices,
            country_of_origin_choices: countryOfOriginChoices,  // added backend field for this - Oct 2024 - will be {code, name} objects
        };
        // keep these as real booleans also
        this.internalCols = {
            show_name: show_name,
            show_part_number: show_part_number,
            show_description: show_description,
            show_quantity: show_quantity,
            show_uom: show_uom,
            show_timeframe: show_timeframe,
            show_need_by_date: show_need_by_date,
            show_current_price: show_current_price,  // we were forcing this to false until Sept 2025, now we honor what is passed in constructor (from backend)
            show_target_price: show_target_price,  // we were forcing this to false until Sept 2025, now we honor what is passed in constructor (from backend)
        }
        console.log('Done with QuoteSheet constructor, vendorCols are:');
        console.log(this.vendorCols);
    }
    /* getVendorCols() { // returns a SHALLOW COPY of this.vendorCols -- used for comparisons
        // NOT a getter... so call like obj.getVendorCols()
        return Object.assign({}, this.vendorCols);
    } */
    getEnabledVendorCols() {  // used in Reply grid (not builder)
        // return an ARRAY of vendorCols from canonical list, filtered to return only those that have enabled = true in this QuoteSheet
        // you make a Map first, then use Array.from(myMap.values()) to return array
        // ALSO WILL MERGE IN CHOICES, if we have them here (shipterms_choices, payterms_choices)
        //      - there is a list of default choices for certain fields, but if we have array here (shipterms_choices, payterms_choices) we'll overwrite choices on those 
        //      - choices will go in 2 places (only really need one...) - in choices key, and in replyGrid.cellEditorParams.values
        //      - why 2 places?  because 'choices' is at top level of vendorCol, must be set in builder, used in reply component.  for consistency here, we'll put in both places
        // later, replace this once you have col definitions (with choices) coming from backend
        let enabledVendorColsMap = new Map();  // use .set(key, value) .has(key) .get(key) .delete(key) .clear() .size
        vendorColsAll.forEach(vcol => {
            if (this.vendorCols[vcol.enableKey]) {  // if this vendorCol is enabled in this QuoteSheet
                enabledVendorColsMap.set(vcol.plainKey, cloneDeep(vcol));  // add it to the map, cloneDeep to make sure we don't mutate unnecessarily
            }
        }); 
        // NOW DO SOME SPECIAL TRANSFORMS / ADD CHOICES -- things that relate to this instance of QuoteSheet
        if (enabledVendorColsMap.has('shipterms')) {
            let shipChoices = this.vendorCols.shipterms_choices;
            if (!!shipChoices && Array.isArray(shipChoices) && shipChoices.length) {  // if you have real shipterms_choices
                let shipCol = enabledVendorColsMap.get('shipterms');
                let shipChoicesCopy = [...shipChoices];  // MAKE A COPY TO AVOID MUTATING
                shipCol['choices'] = shipChoicesCopy;  // get the vcol object for shipterms and set its choices -- we pull from here for some things
                shipCol.replyGrid.cellEditorParams.values = shipChoicesCopy;  // and put them where needed for Reply component
            }
        }
        if (enabledVendorColsMap.has('payterms')) {
            let payChoices = this.vendorCols.payterms_choices;
            if (!!payChoices && Array.isArray(payChoices) && payChoices.length) {  // if you have real payterms_choices
                let payCol = enabledVendorColsMap.get('payterms');
                let payChoicesCopy = [...payChoices];  // MAKE A COPY TO AVOID MUTATING
                payCol['choices'] = payChoicesCopy;  // get the vcol object for payterms and set its choices -- we pull from here for some things
                payCol.replyGrid.cellEditorParams.values = payChoicesCopy;  // and put them where needed for Reply component
            }
        }
        if (enabledVendorColsMap.has('country_of_origin')) {
            const countries = this.countryNames;  // vendorCols.country_of_origin_choices;
            if (!!countries && Array.isArray(countries) && countries.length) {
                // will be array of objects like {code: 'US', name: 'United States'} - we want in 2 different formats in the grid -- list of code strings for values of editor, and refData with single object of {code: name} for display
                let cooCol = enabledVendorColsMap.get('country_of_origin');
                //const cooCodes = cooChoicesCopy.map(cooObj => cooObj.code);  // get just the codes
                //const cooRefData = Object.fromEntries(cooChoicesCopy.map(cooObj => [cooObj.code, cooObj.name]));  // get object of {code: name} for display
                //cooCol['choices'] = cooChoicesCopy;  // get the vcol object for country_of_origin and set its choices -- we pull from here for some things
                cooCol.replyGrid.cellEditorParams.values = [...countries];  // this gets used as the values for the agRichSelectCellEditor
                //cooCol.replyGrid.refData = cooRefData;  // the refData is just the object of {code: name} for display
            }
        }
        // add currency to header of Price (if its enabled)
        // this ONLY works for price column, since that should match our quote sheet currency -- but reply currency may be different
        if (enabledVendorColsMap.has('price')) {
            let priceCol = enabledVendorColsMap.get('price');  // if its enabled, get the vcol from our map
            priceCol.label = priceCol.label + ' (' + this.currency + ')';  // add (USD) (or whatever currency) to the label
        }
        // cannot do this for quoted_price -- it depends on reply! - but you should always show quoted_price going forward
        //if (enabledVendorColsMap.has('quoted_price')) {
            //let quotedPriceCol = enabledVendorColsMap.get('quoted_price');  // if its enabled, get the vcol from our map
            //quotedPriceCol.label = quotedPriceCol.label + ' (' + this.currency + ')';  // add (USD) (or whatever currency) to the label
        //} 
        return Array.from(enabledVendorColsMap.values());
    }
    getInternalCols() {  // returns a SHALLOW COPY of this.internalCols -- used for comparisons
        // NOT a getter... so call like obj.getInternalCols()
        return Object.assign({}, this.internalCols);
    }
    formatForApi() {
        let topLevelData = {
            id: this.id,
            title: this.title,
            instructions: this.instructions,
            currency: this.currency,
            quoted_currency_options_codes: this.quoted_currency_options_codes,
        };
        // combine everything into one non-nested object
        return Object.assign(topLevelData, this.vendorCols, this.internalCols);
    }
    isValid() {
        let requiredFields = ['id', 'title', 'currency'];
        let requiredFieldsOk = requiredFields.every(fld => hasValueOrZero(this[fld]));
        let vendorColsValues = Object.values(this.vendorCols);
        let vendorColsAtLeastOne = vendorColsValues.some(vcol => !!vcol);  // these are booleans, so no need for hasValueOrZero()
        return requiredFieldsOk && vendorColsAtLeastOne;
    }
}


class LinkedProduct {
    constructor({  // since you have the = {} in the args, you can create a new LinkedProduct by just calling new LinkedProduct()
        id = '',
        name = '',
        description = '',
        part_number = '',
        is_variant = false,
        variant_of = '',
        current_price = null,
        current_price_currency = '',
        target_price = null,
        target_price_currency = '',
        uom_temp='',
        url='',  // feb 23 2023 - this is the INTERNAL url
    } = {}) {
        // set all things from args object here with this.arg = arg;
        this.id = id;
        this.name = name;
        this.description = description;
        this.part_number = part_number;
        this.is_variant = is_variant;
        this.variant_of = variant_of;
        this.current_price = toNumberOrNull(current_price);
        this.current_price_currency = current_price_currency;
        this.target_price = toNumberOrNull(target_price);
        this.target_price_currency = target_price_currency;
        this.uom_temp = uom_temp;
        this.url = url;
    }
    //formatForApi() // not needed?
    isValid() {
        // make sure we have id (because this should already be saved), name
        // on higher level, make sure currency matches variant / quoteSheet currencies?
        let requiredValuesOk = !!this.id && !!this.name;
        return requiredValuesOk;
    }
}


export {
    QuoteSheet,
    LinkedProduct,
}